import { useState, useEffect, useMemo, useCallback } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import TableContainer from '../../components/genericTable/TableContainer';
import { columnsData } from './columnsData';
import { Column } from 'react-table';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { Container, Row, Col, Tab, Tabs, Badge, Dropdown } from 'react-bootstrap';
import Btn from '../../components/Btn/Btn';
import { FaPlus, FaFileDownload, FaFileCsv, FaFileExcel } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { GTMsendEvent } from '../../utils/trackingGA4'
import { RoleEnum } from 'src/api';
import Restricted from 'src/components/Restricted/Restricted';
import { useAuth } from 'src/hooks/useAuth';
import { useErrorToast } from 'src/hooks/useErrorToast';
// @ts-ignore
import './userListPage.scss';
import { isMarket } from 'src/utils/markets';
import { Markets } from 'src/types';
import { ReporterStatusEnum } from 'src/api/models/Enums';
import { downloadCSV, downloadXLSX } from 'src/utils';

const UserTabKeys = {
	ACTIVE_USERS: "ACTIVE_USERS",
	AWAITING_APPROVAL_USERS: "AWAITING_APPROVAL_USERS",
}

const UserListPage = () => {
	const { apiClient, handleApi } = useApiClient();
	const [response, setResponse]: [any, any] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const { userRole, canApproveSegnalatori } = useAuth();
	const { t } = useTranslation()
	const { showError } = useErrorToast()
	const [isExporting, setIsExporting] = useState(false);

	const navigate = useNavigate();

	useEffect(() => {
		handleApi(apiClient.userController.getRegisterUsers())
			.then((res) => {
				setResponse(res);
			})
			.catch((err) => {
				setError(err);
				showError(err, i18next.t("users_list"))
			}).finally(() => {
				setLoading(false);
			});
	}, []);

	const awaitingApprovalUsers = useMemo(() => {
		if (!response) return [];
		return response.filter(item => item?.role === RoleEnum.SEGNALATORE
			&& item?.status?.toUpperCase() === ReporterStatusEnum.PENDING);
	}, [response])

	const activeUsers = useMemo(() => {
		if (!response) return [];
		return response.filter(item => {
			return item?.role === RoleEnum.SEGNALATORE && (item?.status?.toUpperCase() !== ReporterStatusEnum.APPROVED)
				? false
				: true;
		});
	}, [response])

	const isAllowed = [RoleEnum.MASTER, RoleEnum.BACKOFFICE, RoleEnum.ADMIN, RoleEnum.AREA_MANAGER].includes(userRole);

	const columns = useMemo(() => {
		const cols = columnsData as Column<Object>[];

		if (isAllowed) {
			return cols;
		}

		return (cols || []).filter(item => item.id !== 'actions');
	}, [userRole]);

	const showUserTabs = isMarket(Markets.it)
		&& ([RoleEnum.ADMIN, RoleEnum.MASTER].includes(userRole) || canApproveSegnalatori);

	const handleExportUsers = useCallback(
		async (format) => {
			try {
				setIsExporting(true);
				const dataToExport = response;

				if (!dataToExport?.length) {
					showError(null, i18next.t('no_data_available_for_download'));
					return;
				}

				const fields = [
					{ key: 'username',                  header: i18next.t('username') },
					{ key: 'given_name',                header: i18next.t('name') },
					{ key: 'family_name',               header: i18next.t('surname') },
					{ key: 'role',                      header: i18next.t('role') },
					{ key: 'business_name',             header: i18next.t('business_name') },
					{ key: 'dealer',                    header: i18next.t('dealer') },
					{ key: 'sales',                     header: i18next.t('sales') },
					{ key: 'group',                     header: i18next.t('group') },
					{ key: 'phone',                     header: i18next.t('phone') },
					{ key: 'phone_backup',              header: i18next.t('phone_backup') },
					{ key: 'email_backup',              header: i18next.t('email_backup') },
					{ key: 'reference_partner',         header: i18next.t('dealer_filiale') },
					{ key: 'reference_sales_dealer',    header: i18next.t('reference_sales_dealer') },
					{ key: 'reference_backoffice',      header: i18next.t('reference_backoffice') },
					{ key: 'company_detail',            header: i18next.t('company_detail') },
					{ key: 'is_active_email',           header: i18next.t('is_active_email') },
					{ key: 'is_active',                 header: i18next.t('is_active') },
					{ key: 'is_salesforce_sales',       header: i18next.t('is_salesforce_sales') },
					{ key: 'visibility_tag',            header: i18next.t('visibility_tag') },
					{ key: 'display_commissions',       header: i18next.t('display_commissions') },
					{ key: 'area_backoffices',          header: i18next.t('area_backoffices') },
					{ key: 'provvigioni_segnalatore',   header: i18next.t('provvigioni_segnalatore') },
					{ key: 'status',                    header: i18next.t('status') },
					{ key: 'iban',                      header: i18next.t('iban') },
					{ key: 'created_date',              header: i18next.t('created_date') },
					{ key: 'last_modified_date',        header: i18next.t('last_modified_date') },
					{ key: 'sub',                       header: i18next.t('user_id') },
				];

				const csvData: any[] = [fields.map(f => f.header)];

				dataToExport.forEach((row: any) => {
					csvData.push(fields.map(({ key }) => {
						const value = row[key];
						if (value === null || value === undefined) return '';
						if (Array.isArray(value)) return value.join(', ');
						if (typeof value === 'string') {
							return value.replace(/\n/g, ' ');
						}
						return String(value);
					}));
				});

				const fileName = i18next.t('users_list').replace(/ /g, '_');

				switch (format) {
					case 'xlsx':
						downloadXLSX(csvData, fileName);
						break;
					case 'csv':
						downloadCSV(csvData, fileName);
						break;
					default:
						console.warn(`[Horizon] Unsupported export format: ${format}`);
						break;
				}

				GTMsendEvent({
					event: 'CTA_download_file',
					evtCat: 'lista_utenti',
					evtAct: 'download',
					evtLab: `${fileName}.${format}`,
				});
			} catch (err) {
				showError(err, i18next.t("export_error"))
			} finally {
				setIsExporting(false);
			}
		},
		[
			handleApi,
			response,
			showError,
		]
	);

	const handleExportDealers = async (format: string) => {
		try {
			setIsExporting(true);
			const dealersUsers = await handleApi(apiClient.userController.getDealersUsers());

			if (!dealersUsers?.length) {
				showError(null, i18next.t('no_data_available_for_download'));
				return;
			}

			const header = [
				i18next.t('username'),
				i18next.t('dealer_filiale'),
				i18next.t('created_date'),
			];

			const csvData: any[] = [header];

			dealersUsers.forEach((row: any) => {
				csvData.push([
					row.username ?? '',
					row.reference_partner ?? '',
					row.created_date ?? '',
				]);
			});

			const fileName = i18next.t('export_dealers_users').replace(/ /g, '_');

			switch (format) {
				case 'xlsx':
					downloadXLSX(csvData, fileName);
					break;
				case 'csv':
					downloadCSV(csvData, fileName);
					break;
				default:
					console.warn(`[Horizon] Unsupported export format: ${format}`);
					break;
			}

			GTMsendEvent({
				event: 'CTA_download_file',
				evtCat: 'lista_dealers_utenti',
				evtAct: 'download',
				evtLab: `${fileName}.${format}`,
			});
		} catch (err) {
			showError(err, i18next.t("export_error"))
		} finally {
			setIsExporting(false);
		}
	};

	return (
		<Container fluid id="user-list-page">
			<Row>
				<Col md={4} className="display-6">
					{t("users_list")}
				</Col>
				<Col md={8} className="mt-4 mt-md-0 d-flex gap-4 justify-content-end align-items-center flex-wrap">
					<Restricted to={[RoleEnum.MASTER, RoleEnum.ADMIN]}>
						<Dropdown className="align-self-center pb-1">
							<Dropdown.Toggle
								disabled={loading || isExporting}
								className="d-flex align-items-center gap-2 pb-2"
							>
								<FaFileDownload />
								{i18next.t('export', 'Export')}
							</Dropdown.Toggle>
							<Dropdown.Menu>
								<Dropdown.Header>{i18next.t('export_users')}</Dropdown.Header>
								<Dropdown.Item onClick={() => handleExportUsers('csv')}>
									<FaFileCsv className="me-2" />{i18next.t('export_as_csv')}
								</Dropdown.Item>
								<Dropdown.Item onClick={() => handleExportUsers('xlsx')}>
									<FaFileExcel className="me-2" />{i18next.t('export_as_excel')}
								</Dropdown.Item>
								<Dropdown.Divider />
								<Dropdown.Header>{i18next.t('export_dealers_users')}</Dropdown.Header>
								<Dropdown.Item onClick={() => handleExportDealers('csv')}>
									<FaFileCsv className="me-2" />{i18next.t('export_as_csv')}
								</Dropdown.Item>
								<Dropdown.Item onClick={() => handleExportDealers('xlsx')}>
									<FaFileExcel className="me-2" />{i18next.t('export_as_excel')}
								</Dropdown.Item>
							</Dropdown.Menu>
						</Dropdown>
					</Restricted>
					<Restricted to={[RoleEnum.MASTER, RoleEnum.BACKOFFICE, RoleEnum.ADMIN, RoleEnum.AREA_MANAGER]}>
						<Btn
							onClick={() => {
								navigate('/manage-users/new')
								GTMsendEvent({
									event: 'CTA_create_new_user',
									evtCat: 'user',
									evtAct: 'create',
									evtLab: 'new_user'
								})
							}}
						>
							<FaPlus />
							{t("create_new_user")}
						</Btn>
					</Restricted>
				</Col>
			</Row>

			{showUserTabs
				? <Tabs
					defaultActiveKey={UserTabKeys.ACTIVE_USERS}
					className="mb-3 mt-3"
					fill
				>
					<Tab
						eventKey={UserTabKeys.ACTIVE_USERS}
						className="tab"
						tabClassName="tab"
						title={<div>
							{i18next.t('active_users_tab')}
							{/* <Badge pill bg="secondary" className="ms-4">{activeUsers?.length || 0}</Badge> */}
						</div>}
					>
						<TableContainer
							dblClickCallback={(row: any) => {
								if (!isAllowed) {
									return;
								}
								navigate(`/manage-users/${row.username}`)
							}}
							columns={columns}
							data={activeUsers}
							loading={loading}
							error={error}
						></TableContainer>
					</Tab>
					<Tab
						eventKey={UserTabKeys.AWAITING_APPROVAL_USERS}
						className="tab"
						tabClassName="tab"
						title={<>
							{i18next.t('awaiting_approval_users_tab')}
							<Badge pill bg="secondary" className="ms-4">{awaitingApprovalUsers?.length || 0}</Badge>
						</>}
					>
						<TableContainer
							dblClickCallback={(row: any) => {
								if (!isAllowed) {
									return;
								}
								navigate(`/manage-users/${row.username}`)
							}}
							columns={columns}
							data={awaitingApprovalUsers}
							loading={loading}
							error={error}
						></TableContainer>
					</Tab>
				</Tabs>
				: (
					<TableContainer
						dblClickCallback={(row: any) => {
							if (!isAllowed) {
								return;
							}
							navigate(`/manage-users/${row.username}`)
						}}
						columns={columns}
						data={activeUsers}
						loading={loading}
						error={error}
					></TableContainer>
				)}
		</Container>
	);
};

export default UserListPage;
